import { computed, Ref, unref } from 'vue'
import { ActualStop, InventoryReceiptType, JobStopType } from '@ironsight/types'
import { useLocationQuery } from '@/api/locations'
import { useLocationOwner } from '@/composables/useLocationOwner'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import { allOf, check, usePermission } from '@/components/common/permission'
import { useJobStopsAndReceiptsPermissions } from './useJobStopsAndReceiptsPermissions'
import { disabledActionMessages as message } from './disabledActionMessages'

export function useJobStopsVisibilityLogic(
  jobStop: Ref<ActualStop | null>,
  jobNumber: Ref<number>,
) {
  const { currentOrg } = useCurrentOrg()
  const locationId = computed(() => jobStop.value?.locationId ?? null)
  const locationQuery = useLocationQuery(locationId)
  const locationOwnerId = computed(() => locationQuery.data.value?.ownerId ?? null)
  const { isLocationOwner } = useLocationOwner(locationOwnerId)
  const { jobActionPermission, dispatcherOrAdminPermission } = useJobStopsAndReceiptsPermissions(
    jobNumber,
    locationOwnerId,
  )

  const hasMyReceiverReceipt = computed(() => {
    const currentOrgId = currentOrg.value?.id
    return (
      !!currentOrgId &&
      !!jobStop.value?.inventoryReceipts.some(
        (receipt) =>
          receipt.receiptType === InventoryReceiptType.Receiver &&
          receipt.ownerOrgId === currentOrgId,
      )
    )
  })

  const shouldShowReceiverReceiptButton = computed(
    () =>
      jobStop.value?.stopType === JobStopType.DropOff &&
      !jobStop.value.isSkipped &&
      !hasMyReceiverReceipt.value,
  )

  const locationOwnershipPermission = computed(() => {
    if (
      !locationId.value ||
      unref(locationQuery.isError) ||
      (locationQuery.data.value && !locationOwnerId.value)
    ) {
      return check(false, message.locationError)
    }
    if (!locationQuery.data.value) {
      return check(false, message.locationLoading)
    }
    return check(isLocationOwner.value, message.locationOwner)
  })

  const {
    permission: receiverReceiptPermission,
    allowed: canReceiveReceiptAtLocation,
    reasons: receiverReceiptDisabledReasons,
  } = usePermission(() =>
    allOf(
      jobActionPermission.value,
      dispatcherOrAdminPermission.value,
      check(!!currentOrg.value?.id, message.organizationContext),
      locationOwnershipPermission.value,
    ),
  )

  const canConfirmReceiverReceipt = computed(
    () => shouldShowReceiverReceiptButton.value && canReceiveReceiptAtLocation.value,
  )

  return {
    shouldShowReceiverReceiptButton,
    receiverReceiptPermission,
    receiverReceiptDisabledReasons,
    canConfirmReceiverReceipt,
  }
}
